import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Typography,
  Box,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  MovieceptionCancelButton,
  MovieceptionErrorButton,
  MovieceptionPrimaryButton,
} from '../components/Buttons/movieception-buttons'

type ConfirmModalProps = {
  open: boolean
  title: string
  description?: string
  /** Extra line rendered in red, e.g. "This can't be undone." */
  warning?: string
  confirmText?: string
  cancelText?: string
  /** Renders the confirm button in the error (red) style. */
  danger?: boolean
  /** Locks the confirm button for N seconds after the modal opens. */
  countdownSeconds?: number
  confirmDisabled?: boolean
  onConfirm: () => void
  onCancel: () => void
}

const ModalContainer = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 12,
    padding: theme.spacing(1),
    minWidth: 360,
  },
}))

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 18,
  fontWeight: 600,
  marginBottom: theme.spacing(1),
}))

const Description = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  color: theme.palette.text.secondary,
}))

const Warning = styled(Typography)(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.error.main,
  marginTop: theme.spacing(2),
}))

const Actions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2),
  gap: theme.spacing(1),
}))

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  open,
  title,
  description,
  warning,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  danger = false,
  countdownSeconds,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => {
  const [remaining, setRemaining] = useState(0)

  // Restart the countdown every time the modal opens.
  useEffect(() => {
    if (!open || !countdownSeconds) {
      setRemaining(0)
      return
    }
    setRemaining(countdownSeconds)
    const timer = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          clearInterval(timer)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(timer)
  }, [open, countdownSeconds])

  const locked = remaining > 0
  const label = locked ? `${confirmText} (${remaining})` : confirmText
  const ConfirmButton = danger
    ? MovieceptionErrorButton
    : MovieceptionPrimaryButton

  return (
    <ModalContainer open={open} onClose={onCancel}>
      <DialogContent>
        <Box>
          <Title>{title}</Title>
          {description && <Description>{description}</Description>}
          {warning && <Warning>{warning}</Warning>}
        </Box>
      </DialogContent>

      <Actions>
        <MovieceptionCancelButton label={cancelText} onClick={onCancel} />

        <ConfirmButton
          label={label}
          onClick={onConfirm}
          disabled={confirmDisabled || locked}
        />
      </Actions>
    </ModalContainer>
  )
}
